/**
 * Pure pieces of the admin bundle rules tab (MOT-387): the shapes
 * `GET /admin/bundle-rebates` returns, a plain-words summary of a qualifier,
 * the status filter with its counts, and the PUT body an edit produces.
 * Mirrors `src/types/bundleRebate.ts` and `src/validators/bundleRebate.ts`.
 */

export type BundleQualifier =
  | { kind: 'product'; productIds: string[] }
  | { kind: 'brand_family'; brand: string; family: string }
  | { kind: 'mount'; mount: string; productType: 'camera' | 'lens' }
  | { kind: 'category'; brand: string; category: string }
  | {
      kind: 'compatible_with';
      productId: string;
      association: 'compatible_lens' | 'compatible_body' | 'compatible_accessory';
    }
  | { kind: 'any_of'; qualifiers: BundleQualifier[] };

export type BundleQualifierKind = BundleQualifier['kind'];
export type BundleRuleStatus = 'active' | 'needs_review' | 'disabled';
export type BundleStacking = 'exclusive' | 'stacks_with_instant' | 'stacks_with_all';

export interface BundleRule {
  id: string;
  brand: string;
  name: string;
  organizationId: string | null;
  offerProductIds: string[];
  qualifier: BundleQualifier | null;
  instantRebate: number;
  startAt: string | null;
  endAt: string | null;
  stacking: BundleStacking;
  source: 'sheet' | 'manual';
  sourceSubmissionId?: string | null;
  status: BundleRuleStatus;
  rawCondition: string;
  rawOfferProduct?: string;
  reviewNotes?: string | null;
  qualifierOverridden?: boolean;
  updatedAt?: string;
}

export type BundleRuleFilter = 'all' | BundleRuleStatus;

export const STACKING_LABELS: Record<BundleStacking, string> = {
  exclusive: 'Exclusive (best single rule only)',
  stacks_with_instant: 'Stacks with the instant rebate',
  stacks_with_all: 'Stacks with everything',
};

export const QUALIFIER_KIND_LABELS: Record<BundleQualifierKind, string> = {
  product: 'Named products',
  brand_family: 'A brand family ("any Z Series")',
  mount: 'A mount ("any FE lens")',
  category: "A brand's category",
  compatible_with: 'What the catalog links to one product',
  any_of: 'Any one of several (JSON)',
};

const ASSOCIATION_LABELS: Record<string, string> = {
  compatible_lens: 'lens',
  compatible_body: 'camera body',
  compatible_accessory: 'accessory',
};

/** One line saying what must be in the cart with the offer item. */
export function describeQualifier(qualifier: BundleQualifier | null | undefined): string {
  if (!qualifier) return 'No qualifier yet';
  switch (qualifier.kind) {
    case 'product':
      return `With ${qualifier.productIds.length === 1 ? 'product' : 'any of'} ${qualifier.productIds.join(', ')}`;
    case 'brand_family':
      return `With any ${qualifier.brand} ${qualifier.family}`;
    case 'mount':
      return `With any ${qualifier.mount} ${qualifier.productType}`;
    case 'category':
      return `With any ${qualifier.brand} ${qualifier.category}`;
    case 'compatible_with':
      return `With any ${ASSOCIATION_LABELS[qualifier.association] || qualifier.association} linked to ${qualifier.productId}`;
    case 'any_of':
      return qualifier.qualifiers.map((q) => describeQualifier(q)).join(' · or ');
    default:
      return 'Unknown qualifier';
  }
}

export function countByStatus(rules: BundleRule[]): Record<BundleRuleFilter, number> {
  const counts: Record<BundleRuleFilter, number> = { all: rules.length, active: 0, needs_review: 0, disabled: 0 };
  for (const rule of rules) counts[rule.status] = (counts[rule.status] || 0) + 1;
  return counts;
}

/** Status filter plus a search over name, brand, sheet text and ids. */
export function filterRules(rules: BundleRule[], filter: BundleRuleFilter, search: string): BundleRule[] {
  const q = search.trim().toLowerCase();
  return rules.filter((rule) => {
    if (filter !== 'all' && rule.status !== filter) return false;
    if (!q) return true;
    return [rule.name, rule.brand, rule.rawCondition, rule.rawOfferProduct || '', rule.id, ...rule.offerProductIds]
      .some((text) => text.toLowerCase().includes(q));
  });
}

/** The editable part of a rule, as the dialog holds it. */
export interface BundleRuleDraft {
  name: string;
  stacking: BundleStacking;
  /** `needs_review` is only ever a starting point; the admin picks active or disabled. */
  status: BundleRuleStatus;
  qualifier: BundleQualifier | null;
}

export const draftFromRule = (rule: BundleRule): BundleRuleDraft => ({
  name: rule.name,
  stacking: rule.stacking,
  status: rule.status,
  qualifier: rule.qualifier,
});

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * The `PUT /admin/bundle-rebates/:id` body for an edit: only what changed, so
 * an untouched qualifier is never re-sent (sending one marks it admin-edited).
 * Null when nothing changed.
 */
export function updateBody(rule: BundleRule, draft: BundleRuleDraft): Record<string, unknown> | null {
  const body: Record<string, unknown> = {};
  const name = draft.name.trim();
  if (name && name !== rule.name) body.name = name;
  if (draft.stacking !== rule.stacking) body.stacking = draft.stacking;
  if (draft.qualifier && !sameJson(draft.qualifier, rule.qualifier)) body.qualifier = draft.qualifier;
  if (draft.status !== rule.status && draft.status !== 'needs_review') body.status = draft.status;
  return Object.keys(body).length ? body : null;
}

/** Why the qualifier cannot be saved as entered, or null. Mirrors the server's Zod limits. */
export function qualifierProblem(qualifier: BundleQualifier | null): string | null {
  if (!qualifier) return null;
  const blank = (v: string | undefined) => !v || !v.trim();
  switch (qualifier.kind) {
    case 'product':
      return qualifier.productIds.length === 0 ? 'Add at least one product id.' : null;
    case 'brand_family':
      return blank(qualifier.brand) || blank(qualifier.family) ? 'Brand and family are both required.' : null;
    case 'mount':
      return blank(qualifier.mount) ? 'Name the mount.' : null;
    case 'category':
      return blank(qualifier.brand) || blank(qualifier.category) ? 'Brand and category are both required.' : null;
    case 'compatible_with':
      return blank(qualifier.productId) ? 'Name the product whose links qualify.' : null;
    case 'any_of':
      return qualifier.qualifiers.length === 0 ? 'List at least one qualifier.' : null;
    default:
      return 'Unknown qualifier kind.';
  }
}

/** An empty qualifier of one kind, carrying over the brand when the old one had it. */
export function blankQualifier(kind: BundleQualifierKind, brand: string): BundleQualifier {
  switch (kind) {
    case 'product':
      return { kind, productIds: [] };
    case 'brand_family':
      return { kind, brand, family: '' };
    case 'mount':
      return { kind, mount: '', productType: 'lens' };
    case 'category':
      return { kind, brand, category: '' };
    case 'compatible_with':
      return { kind, productId: '', association: 'compatible_lens' };
    case 'any_of':
    default:
      return { kind: 'any_of', qualifiers: [] };
  }
}
